import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subWeeks,
  subMonths,
  subQuarters,
  differenceInDays,
  subDays,
  format,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import type { PeriodPreset, DateRange } from '../types';

const TIMEZONE = 'America/Montreal';

/**
 * Get the current date in the configured timezone
 */
function getNow(): Date {
  return toZonedTime(new Date(), TIMEZONE);
}

/**
 * Get the date range for a period preset
 */
export function getPeriodDateRange(
  preset: PeriodPreset,
  customRange?: DateRange
): DateRange {
  const now = getNow();

  switch (preset) {
    case 'this_week':
      return {
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case 'last_week': {
      const lastWeek = subWeeks(now, 1);
      return {
        from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    }
    case 'this_month':
      return {
        from: startOfMonth(now),
        to: endOfMonth(now),
      };
    case 'last_month': {
      const lastMonth = subMonths(now, 1);
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth),
      };
    }
    case 'this_quarter':
      return {
        from: startOfQuarter(now),
        to: endOfQuarter(now),
      };
    case 'last_quarter': {
      const lastQuarter = subQuarters(now, 1);
      return {
        from: startOfQuarter(lastQuarter),
        to: endOfQuarter(lastQuarter),
      };
    }
    case 'custom':
      if (customRange) {
        return customRange;
      }
      // Default to this month if no custom range provided
      return {
        from: startOfMonth(now),
        to: endOfMonth(now),
      };
    default:
      return {
        from: startOfMonth(now),
        to: endOfMonth(now),
      };
  }
}

/**
 * Calculate the previous period range based on the current range
 * For standard periods, this returns the equivalent previous period
 * For custom ranges, this returns the same duration immediately before
 */
export function getPreviousPeriodRange(
  currentRange: DateRange,
  preset: PeriodPreset
): DateRange {
  switch (preset) {
    case 'this_week':
    case 'last_week': {
      // Previous week of same type
      const daysBack = 7;
      return {
        from: subDays(currentRange.from, daysBack),
        to: subDays(currentRange.to, daysBack),
      };
    }
    case 'this_month':
    case 'last_month': {
      // Previous month
      return {
        from: startOfMonth(subMonths(currentRange.from, 1)),
        to: endOfMonth(subMonths(currentRange.from, 1)),
      };
    }
    case 'this_quarter':
    case 'last_quarter': {
      // Previous quarter
      return {
        from: startOfQuarter(subQuarters(currentRange.from, 1)),
        to: endOfQuarter(subQuarters(currentRange.from, 1)),
      };
    }
    case 'custom':
    default: {
      // For custom ranges, calculate same duration before
      const durationDays = differenceInDays(currentRange.to, currentRange.from);
      return {
        from: subDays(currentRange.from, durationDays + 1),
        to: subDays(currentRange.from, 1),
      };
    }
  }
}

/**
 * Format a date range for display
 * Examples: "Jan 1 - Jan 31, 2025" or "Jan 1, 2025 - Feb 15, 2025"
 */
export function formatDateRange(range: DateRange): string {
  const fromYear = range.from.getFullYear();
  const toYear = range.to.getFullYear();

  if (fromYear === toYear) {
    // Same year - show year only at the end
    const fromStr = format(range.from, 'MMM d');
    const toStr = format(range.to, 'MMM d, yyyy');
    return `${fromStr} - ${toStr}`;
  } else {
    // Different years - show year on both
    const fromStr = format(range.from, 'MMM d, yyyy');
    const toStr = format(range.to, 'MMM d, yyyy');
    return `${fromStr} - ${toStr}`;
  }
}

/**
 * Format a date for API calls (ISO format: YYYY-MM-DD)
 */
export function formatDateForApi(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Calculate percentage change between two values
 * Returns the percentage change from previous to current
 * Positive means increase, negative means decrease
 */
export function calculatePercentageChange(
  current: number,
  previous: number
): number {
  if (previous === 0) {
    if (current === 0) return 0;
    return current > 0 ? 100 : -100; // Treat as 100% change if going from 0
  }
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Calculate absolute points change (for rates like win rate)
 * Returns the difference in percentage points
 */
export function calculatePointsChange(
  current: number,
  previous: number
): number {
  return Math.round(current - previous);
}

/**
 * Get human-readable label for period preset
 */
export function getPeriodLabel(preset: PeriodPreset): string {
  const labels: Record<PeriodPreset, string> = {
    this_week: 'This Week',
    last_week: 'Last Week',
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    last_quarter: 'Last Quarter',
    custom: 'Custom',
  };
  return labels[preset];
}

/**
 * All available period presets
 */
export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'custom', label: 'Custom Range' },
];
